import {
  ApiOutlined,
  FileSearchOutlined,
  LogoutOutlined,
  NotificationOutlined,
  SettingOutlined,
  LineChartOutlined,
  SearchOutlined,
  UserSwitchOutlined,
  CustomerServiceOutlined,
  CommentOutlined,
  BellOutlined,
  UserOutlined
} from '@ant-design/icons';
import { Layout, Menu, Tooltip } from 'antd';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AdminTools from './AdminTools';
import { MenuItemType, SubMenuType } from 'antd/lib/menu/hooks/useItems';
import Discord from '../assets/discord-white.svg';
import Slack from '../assets/slack-white.png';
import { AccountSummary } from '../App';
const { Sider } = Layout;

interface SidebarProps {
  item: string;
  admin: boolean;
  envId: string;
  setEnvId: (envId: string) => void;
  accountSummary: AccountSummary;
}

const Sidebar: React.FC<SidebarProps> = ({
  item,
  admin,
  envId,
  setEnvId,
  accountSummary
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [adminPopupVisible, setAdminPopupVisible] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const onCollapse = (collapsed: boolean): void => {
    setCollapsed(collapsed);
  };

  const adminMenuItems: MenuItemType[] = [
    {
      key: 'Admin',
      icon: <UserSwitchOutlined />,
      onClick: () => setAdminPopupVisible(true),
      label: 'Admin'
    }
  ];

  const getNavbarLockMessage = (): string => {
    if (location.pathname.endsWith('/onboarding')) {
      return "We'll get there in a second 🏃";
    } else {
      return "Let's build and send a notification. We'll show you the rest in a minute. Promise 🤞";
    }
  };

  const regularMenuItems: MenuItemType[] = [
    {
      key: 'Notifications',
      icon: <NotificationOutlined />,
      label: (
        <Tooltip
          title={
            location.pathname.endsWith('/onboarding')
              ? getNavbarLockMessage()
              : ''
          }
          placement="right"
          mouseEnterDelay={0.1}
          color="magenta"
        >
          <div>Notifications</div>
        </Tooltip>
      ),
      disabled:
        location.pathname.endsWith('/onboarding') &&
        !accountSummary.hasNotification,
      onClick: () => {
        history.push(`/${envId}/notifications`);
      }
    },
    {
      key: 'InApp',
      icon: <BellOutlined />,
      label: (
        <Tooltip
          title={!accountSummary.hasInApp ? getNavbarLockMessage() : ''}
          placement="right"
          mouseEnterDelay={0.1}
          color="magenta"
        >
          <div>In-App Widget</div>
        </Tooltip>
      ),
      disabled: !accountSummary.hasInApp,
      onClick: () => {
        history.push(`/${envId}/inapp`);
      }
    },
    {
      key: 'Users',
      icon: <UserOutlined />,
      label: (
        <Tooltip
          title={
            !accountSummary.hasUsers || !accountSummary.hasNotification
              ? getNavbarLockMessage()
              : ''
          }
          placement="right"
          mouseEnterDelay={0.1}
          color="magenta"
        >
          <div>Users</div>
        </Tooltip>
      ),
      disabled: !accountSummary.hasUsers || !accountSummary.hasNotification,
      onClick: () => {
        history.push(`/${envId}/users`);
      }
    },
    {
      key: 'Logs',
      icon: <SearchOutlined />,
      label: (
        <Tooltip
          title={
            !accountSummary.hasUsers || !accountSummary.hasNotification
              ? getNavbarLockMessage()
              : ''
          }
          placement="right"
          mouseEnterDelay={0.1}
          color="magenta"
        >
          <div>Logs</div>
        </Tooltip>
      ),
      disabled: !accountSummary.hasUsers || !accountSummary.hasNotification,
      onClick: () => {
        history.push(`/${envId}/logs`);
      }
    },
    {
      key: 'Insights',
      icon: <LineChartOutlined />,
      label: (
        <Tooltip
          title={
            !accountSummary.hasUsers || !accountSummary.hasNotification
              ? getNavbarLockMessage()
              : ''
          }
          placement="right"
          mouseEnterDelay={0.1}
          color="magenta"
        >
          <div>Insights</div>
        </Tooltip>
      ),
      disabled: !accountSummary.hasUsers || !accountSummary.hasNotification,
      onClick: () => {
        history.push(`/${envId}/insights`);
      }
    },
    {
      key: 'Environments',
      icon: <ApiOutlined />,
      label: (
        <Tooltip
          title={
            !accountSummary.hasUsers || !accountSummary.hasNotification
              ? getNavbarLockMessage()
              : ''
          }
          placement="right"
          mouseEnterDelay={0.1}
          color="magenta"
        >
          <div>Environments</div>
        </Tooltip>
      ),
      disabled: !accountSummary.hasUsers || !accountSummary.hasNotification,
      onClick: () => {
        history.push(`/${envId}/environments`);
      }
    },
    {
      key: 'Settings',
      icon: <SettingOutlined />,
      label: (
        <Tooltip
          title={
            !accountSummary.hasUsers || !accountSummary.hasNotification
              ? getNavbarLockMessage()
              : ''
          }
          placement="right"
          mouseEnterDelay={0.1}
          color="magenta"
        >
          <div>Settings</div>
        </Tooltip>
      ),
      disabled: !accountSummary.hasUsers || !accountSummary.hasNotification,
      onClick: () => {
        history.push(`/${envId}/settings`);
      }
    },
    {
      key: 'Support',
      icon: <CustomerServiceOutlined />,
      label: 'Support',
      children: [
        {
          key: 'Documentation',
          icon: <FileSearchOutlined />,
          label: (
            <a
              href="https://docs.notificationapi.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Documentation
            </a>
          )
        },
        {
          key: 'Contact Us',
          icon: <CommentOutlined />,
          label: (
            <a
              href="https://www.notificationapi.com/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Us
            </a>
          )
        },
        {
          key: 'Slack',
          icon: <img src={Slack} alt="Slack" width={16} />,
          label: (
            <a
              href="https://join.slack.com/t/notificationapi-comm/shared_invite/zt-ygbwiyip-6eA~A8pxmhGZpk~B14BL7w"
              target="_blank"
              rel="noopener noreferrer"
            >
              Slack
            </a>
          )
        },
        {
          key: 'Discord',
          icon: <img src={Discord} alt="Discord" width={16} />,
          label: (
            <a
              href="https://discord.gg/6ZUVBMgyf5"
              target="_blank"
              rel="noopener noreferrer"
            >
              Discord
            </a>
          )
        }
      ]
    } as SubMenuType,
    {
      key: 'Logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => {
        history.push('/signout');
      }
    }
  ];

  const visibleMenuItems = admin
    ? adminMenuItems.concat(regularMenuItems)
    : regularMenuItems;

  return (
    <>
      <Sider
        collapsed={collapsed}
        collapsible
        onCollapse={onCollapse}
        breakpoint="lg"
        style={{
          paddingTop: '69px',
          minHeight: '100vh'
        }}
      >
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[item]}
          items={visibleMenuItems}
        />
      </Sider>
      {admin && (
        <AdminTools
          open={adminPopupVisible}
          setOpen={setAdminPopupVisible}
          envId={envId}
          setEnvId={setEnvId}
        />
      )}
    </>
  );
};

export default Sidebar;
